import { privacy } from "@redotech/schema-privacy";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

// ==========================
// Canonical Hazmat values
// (Full EasyPost set)
// ==========================

// https://docs.easypost.com/docs/shipments/options

export const Hazmat = {
  // FedEx + DHL eCommerce
  PRIMARY_CONTAINED: "PRIMARY_CONTAINED",
  PRIMARY_PACKED: "PRIMARY_PACKED",
  PRIMARY: "PRIMARY",
  SECONDARY_CONTAINED: "SECONDARY_CONTAINED",
  SECONDARY_PACKED: "SECONDARY_PACKED",
  SECONDARY: "SECONDARY",
  ORMD: "ORMD",
  LITHIUM: "LITHIUM",

  // FedEx + DHL + USPS
  LIMITED_QUANTITY: "LIMITED_QUANTITY",

  // USPS-only / extended
  AIR_ELIGIBLE_ETHANOL: "AIR_ELIGIBLE_ETHANOL",
  CLASS_1: "CLASS_1",
  CLASS_3: "CLASS_3",
  CLASS_7: "CLASS_7",
  CLASS_8_CORROSIVE: "CLASS_8_CORROSIVE",
  CLASS_8_WET_BATTERY: "CLASS_8_WET_BATTERY",
  CLASS_9_NEW_LITHIUM_INDIVIDUAL: "CLASS_9_NEW_LITHIUM_INDIVIDUAL",
  CLASS_9_USED_LITHIUM: "CLASS_9_USED_LITHIUM",
  CLASS_9_NEW_LITHIUM_DEVICE: "CLASS_9_NEW_LITHIUM_DEVICE",
  CLASS_9_DRY_ICE: "CLASS_9_DRY_ICE",
  CLASS_9_UNMARKED_LITHIUM: "CLASS_9_UNMARKED_LITHIUM",
  CLASS_9_MAGNETIZED: "CLASS_9_MAGNETIZED",
  DIVISION_4_1: "DIVISION_4_1",
  DIVISION_5_1: "DIVISION_5_1",
  DIVISION_5_2: "DIVISION_5_2",
  DIVISION_6_1: "DIVISION_6_1",
  DIVISION_6_2: "DIVISION_6_2",
  EXCEPTED_QUANTITY_PROVISION: "EXCEPTED_QUANTITY_PROVISION",
  /** Ground-transport-only hazmat not covered by a more specific code; cannot ship by air. */
  GROUND_ONLY: "GROUND_ONLY",
  /** Air-eligible consumer commodity (non-flammable aerosols, limited-quantity flammables/toxics, misc hazmat) meeting the ID8000 threshold. */
  ID8000: "ID8000",
  LIGHTERS: "LIGHTERS",
  SMALL_QUANTITY_PROVISION: "SMALL_QUANTITY_PROVISION",
} as const;

export type Hazmat = (typeof Hazmat)[keyof typeof Hazmat];

const HazmatSchema_ = createEnum(Hazmat, { enumName: "Hazmat" }).meta({
  privacy: privacy.nonPii,
});
export const HazmatSchema: z.ZodType<Hazmat> = HazmatSchema_;

// ==========================
// Carrier-supported subsets
// (what Redo exposes)
// ==========================

export const CARRIER_HAZMAT = {
  FEDEX_DHL: [
    Hazmat.PRIMARY_CONTAINED,
    Hazmat.PRIMARY_PACKED,
    Hazmat.PRIMARY,
    Hazmat.SECONDARY_CONTAINED,
    Hazmat.SECONDARY_PACKED,
    Hazmat.SECONDARY,
  ],
  DHL_ECOMMERCE: [
    Hazmat.ORMD,
    Hazmat.LIMITED_QUANTITY,
    Hazmat.SMALL_QUANTITY_PROVISION,
  ],
  USPS: [Hazmat.CLASS_9_UNMARKED_LITHIUM, Hazmat.ID8000, Hazmat.GROUND_ONLY],
  ALL_CARRIERS: [Hazmat.CLASS_9_DRY_ICE],
} as const;

export type FedexDhlHazmat = (typeof CARRIER_HAZMAT.FEDEX_DHL)[number];

export type DhlEcommerceOnlyHazmat =
  (typeof CARRIER_HAZMAT.DHL_ECOMMERCE)[number];

export type DhlEcommerceHazmat = FedexDhlHazmat | DhlEcommerceOnlyHazmat;

export type UspsHazmat = (typeof CARRIER_HAZMAT.USPS)[number];

export type SupportedHazmat =
  | FedexDhlHazmat
  | DhlEcommerceOnlyHazmat
  | UspsHazmat
  | (typeof CARRIER_HAZMAT.ALL_CARRIERS)[number];

export function isSupportedHazmat(hazmat: Hazmat): hazmat is SupportedHazmat {
  return Object.values(CARRIER_HAZMAT)
    .flat()
    .includes(hazmat as SupportedHazmat);
}

// ==========================
// Display labels (only supported)
// ==========================

export const HAZMAT_DISPLAY_LABELS = {
  [Hazmat.PRIMARY_CONTAINED]: "Lithium Metal Batteries Contained in Equipment",
  [Hazmat.PRIMARY_PACKED]: "Lithium Metal Batteries Packed with Equipment",
  [Hazmat.PRIMARY]: "Lithium Metal Batteries Stand-Alone",

  [Hazmat.SECONDARY_CONTAINED]: "Lithium-ion Batteries Contained in Equipment",
  [Hazmat.SECONDARY_PACKED]: "Lithium-ion Batteries Packed with Equipment",
  [Hazmat.SECONDARY]: "Lithium-ion Batteries Stand-Alone",

  [Hazmat.ORMD]: "Limited Quantity",
  [Hazmat.LIMITED_QUANTITY]: "Limited Quantity, for Canada or Mexico only",
  [Hazmat.SMALL_QUANTITY_PROVISION]: "Small Quantity Provision",

  [Hazmat.CLASS_9_UNMARKED_LITHIUM]: "Lithium Batteries (Class 9 Unmarked)",
  [Hazmat.CLASS_9_DRY_ICE]: "Dry Ice",

  [Hazmat.ID8000]: "Consumer Commodity (Aerosols, Spray Paint, Hair Spray)",
  [Hazmat.GROUND_ONLY]: "Ground Only Hazardous Materials (Flammable Aerosols)",
} satisfies Record<SupportedHazmat, string>;

// ==========================
// DHL eCommerce content categories
// ==========================

// https://developer.dhl.com/api-reference/references-dhl-ecommerce-americas#get-started-section/tax-id-types

/*
The mapping from what's passed to EP to what's sent to DHL is:

    LITHIUM -> 01
    PRIMARY_CONTAINED -> 01
    PRIMARY_PACKED -> 02
    PRIMARY -> 03
    SECONDARY_CONTAINED -> 04
    SECONDARY_PACKED -> 05
    SECONDARY -> 06
    ORMD -> 08
    SMALL_QUANTITY_PROVISION -> 09
    LIMITED_QUANTITY -> 40
    */

export const DhlEcommerceContentCategory = {
  LITHIUM_METAL_CONTAINED: "01",
  LITHIUM_METAL_PACKED: "02",
  LITHIUM_METAL_STANDALONE: "03",
  LITHIUM_ION_CONTAINED: "04",
  LITHIUM_ION_PACKED: "05",
  LITHIUM_ION_STANDALONE: "06",
  LIMITED_QUANTITY: "08",
  SMALL_QUANTITY_PROVISION: "09",
  LIMITED_QUANTITY_CANADA_MEXICO: "40",
} as const;

const supportedDhlEcommerceContentCategories = [
  DhlEcommerceContentCategory.LITHIUM_METAL_CONTAINED,
  DhlEcommerceContentCategory.LITHIUM_METAL_PACKED,
  DhlEcommerceContentCategory.LITHIUM_METAL_STANDALONE,
  DhlEcommerceContentCategory.LITHIUM_ION_CONTAINED,
  DhlEcommerceContentCategory.LITHIUM_ION_PACKED,
  DhlEcommerceContentCategory.LITHIUM_ION_STANDALONE,
  DhlEcommerceContentCategory.LIMITED_QUANTITY,
  DhlEcommerceContentCategory.SMALL_QUANTITY_PROVISION,
  DhlEcommerceContentCategory.LIMITED_QUANTITY_CANADA_MEXICO,
] as const;

export type SupportedDhlEcommerceContentCategory =
  (typeof supportedDhlEcommerceContentCategories)[number];

export const DHL_ECOMMERCE_HAZMAT_MAP = {
  [Hazmat.PRIMARY_CONTAINED]:
    DhlEcommerceContentCategory.LITHIUM_METAL_CONTAINED,
  [Hazmat.PRIMARY_PACKED]: DhlEcommerceContentCategory.LITHIUM_METAL_PACKED,
  [Hazmat.PRIMARY]: DhlEcommerceContentCategory.LITHIUM_METAL_STANDALONE,
  [Hazmat.SECONDARY_CONTAINED]:
    DhlEcommerceContentCategory.LITHIUM_ION_CONTAINED,
  [Hazmat.SECONDARY_PACKED]: DhlEcommerceContentCategory.LITHIUM_ION_PACKED,
  [Hazmat.SECONDARY]: DhlEcommerceContentCategory.LITHIUM_ION_STANDALONE,
  [Hazmat.ORMD]: DhlEcommerceContentCategory.LIMITED_QUANTITY,
  [Hazmat.SMALL_QUANTITY_PROVISION]:
    DhlEcommerceContentCategory.SMALL_QUANTITY_PROVISION,
  [Hazmat.LIMITED_QUANTITY]:
    DhlEcommerceContentCategory.LIMITED_QUANTITY_CANADA_MEXICO,
} satisfies Record<DhlEcommerceHazmat, SupportedDhlEcommerceContentCategory>;

export function isSupportedDhlHazmat(
  hazmat: Hazmat,
): hazmat is DhlEcommerceHazmat {
  return Object.keys(DHL_ECOMMERCE_HAZMAT_MAP).includes(hazmat);
}

const FEDEX_HAZMAT_MAP = {
  [Hazmat.PRIMARY_CONTAINED]: "LITHIUM_METAL_CONTAINED",
  [Hazmat.PRIMARY_PACKED]: "LITHIUM_METAL_PACKED",
  [Hazmat.PRIMARY]: "LITHIUM_METAL_CONTAINED",
  [Hazmat.SECONDARY_CONTAINED]: "LITHIUM_ION_CONTAINED",
  [Hazmat.SECONDARY_PACKED]: "LITHIUM_ION_PACKED",
  [Hazmat.SECONDARY]: "LITHIUM_ION_CONTAINED",
} satisfies Record<FedexDhlHazmat, string>;

export function isSupportedFedExHazmat(
  hazmat: Hazmat,
): hazmat is FedexDhlHazmat {
  return (
    Object.keys(FEDEX_HAZMAT_MAP).includes(hazmat) ||
    hazmat === Hazmat.CLASS_9_DRY_ICE
  );
}

export function isSupportedDirectFedExHazmat(
  hazmat: Hazmat,
): hazmat is typeof Hazmat.CLASS_9_DRY_ICE {
  return hazmat === Hazmat.CLASS_9_DRY_ICE;
}

/*
 Easypost has told us that this is their mapping of Hazmat codes to FedEx hazmat codes
    PRIMARY_CONTAINED -> LITHIUM_METAL_CONTAINED
    PRIMARY_PACKED -> LITHIUM_METAL_PACKED
    PRIMARY -> LITHIUM_METAL_CONTAINED
    SECONDARY_CONTAINED -> LITHIUM_ION_CONTAINED
    SECONDARY_PACKED -> LITHIUM_ION_PACKED
    SECONDARY -> LITHIUM_ION_CONTAINED
    ORMD -> ORM_D
    LITHIUM -> LITHIUM_METAL_CONTAINED
 */
